/**
 * La primera vez que alguien entra en un aparato, la app le pide sus permisos
 * UNO POR UNO, cada uno con su para que: primero los avisos, despues la camara.
 * Es lo que hacen las apps grandes, y por que se hace asi —y no pidiendolos
 * solos al abrir— esta en `lib/permissions-tour.ts`.
 *
 * Solo pregunta lo que de verdad sirve aqui:
 *   - Los avisos, a quien recibe alguno (el empleado no recibe ninguno) y no los
 *     apago a proposito. En iPhone o iPad en Safari no se pueden pedir: el paso
 *     ofrece instalar la app, que es lo unico que los hace llegar.
 *   - La camara, donde se escanea con ella (no en la computadora del mostrador,
 *     que tiene lector USB).
 * Lo dado, lo negado y lo que el aparato no tiene se salta. Lo negado lo
 * resuelve Configuracion, con la guia para desbloquearlo.
 *
 * Sale una vez por persona y aparato: "Ahora no" o cerrar la hoja tambien
 * cuentan como respuesta. Despues de eso, todo queda en Configuracion.
 *
 * Espera su turno con el instalador (`lib/install.ts`): primero instalar, luego
 * los permisos, nunca las dos hojas encimadas.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Bell, Camera, Download } from 'lucide-react';
import { readDevicePermissions } from '../lib/device-permissions.js';
import { currentEnv, iosDeviceName } from '../lib/device.js';
import { openInstaller, useInstallerBusy, useInstallerOpen } from '../lib/install.js';
import { receivesNotices } from '../lib/notification-target.js';
import { markTourSeen, planTour, setTourActive, tourSeen, type TourStep } from '../lib/permissions-tour.js';
import { enablePush, pushTurnedOff } from '../lib/push.js';
import { useCameraScanAvailable } from '../lib/scanner.js';
import { useSession } from '../lib/session.js';
import { useToast } from './toast.js';
import { Button, Modal } from './ui.js';

interface StepCopy {
  icon: ReactNode;
  title: string;
  body: string;
  /** Lo que va a pasar al tocar: el cuadro del sistema no debe tomar por sorpresa. */
  hint: string;
  action: string;
}

function copyFor(step: TourStep): StepCopy {
  switch (step) {
    case 'notifications':
      return {
        icon: <Bell className="size-7" />,
        title: 'Avisos en este aparato',
        body: 'Te avisamos aunque la app esté cerrada: el reporte del día, lo que se está acabando y las entradas por firmar.',
        hint: 'Tu aparato te va a preguntar. Toca «Permitir».',
        action: 'Activar avisos',
      };
    case 'install-for-notifications':
      return {
        icon: <Download className="size-7" />,
        title: `Avisos en tu ${iosDeviceName(currentEnv())}`,
        body: `En ${iosDeviceName(currentEnv())} los avisos solo llegan con la app instalada en tu pantalla de inicio. Toma tres toques.`,
        hint: 'Después ábrela desde su ícono y ahí mismo te pedimos los avisos.',
        action: 'Cómo instalarla',
      };
    case 'camera':
      return {
        icon: <Camera className="size-7" />,
        title: 'Cámara para escanear',
        body: 'Para leer códigos de barras con la cámara de este aparato, sin lector.',
        hint: 'Solo se enciende cuando tú abres el escáner.',
        action: 'Permitir cámara',
      };
  }
}

export function PermissionsTour(): ReactNode {
  const { user, can } = useSession();
  const toast = useToast();
  const userId = user?.id ?? null;
  const cameraUsed = useCameraScanAvailable();
  const installerBusy = useInstallerBusy();
  const installerOpen = useInstallerOpen();
  const wantsNotifications = receivesNotices(can) && !pushTurnedOff();

  /*
   * Los pasos se fijan al arrancar y ya no se recalculan: dar un permiso no
   * debe mover la cuenta "paso 2 de 2" ni cambiar la hoja bajo el dedo.
   */
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [index, setIndex] = useState(0);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (userId === null || steps !== null || installerBusy || tourSeen(userId)) return;
    let alive = true;
    void readDevicePermissions().then((permissions) => {
      if (alive) setSteps(planTour(permissions, { notifications: wantsNotifications, camera: cameraUsed }));
    });
    return () => {
      alive = false;
    };
  }, [userId, steps, installerBusy, wantsNotifications, cameraUsed]);

  const step = steps?.[index] ?? null;
  const running = step !== null;
  useEffect(() => {
    setTourActive(running);
    return () => setTourActive(false);
  }, [running]);

  if (!running || userId === null || installerOpen) return null;
  const total = steps?.length ?? 0;

  const finish = (): void => {
    markTourSeen(userId);
    setIndex(total);
  };
  const next = (): void => {
    if (index + 1 >= total) finish();
    else setIndex(index + 1);
  };

  const allow = async (): Promise<void> => {
    switch (step) {
      case 'notifications': {
        // Lo PRIMERO del toque, sin ninguna espera antes: Safari solo enseña el
        // cuadro si la peticion sale directo del gesto (`enablePush`).
        const pending = enablePush();
        setAsking(true);
        const outcome = await pending;
        setAsking(false);
        if (outcome.ok) toast.success('Avisos encendidos en este aparato.');
        else if (outcome.reason === 'denied') toast.show('Quedaron apagados. Se activan desde Configuración.');
        else if (outcome.reason === 'error') toast.error(outcome.message);
        next();
        return;
      }
      case 'install-for-notifications':
        // El recorrido se aparta mientras el instalador esta en pantalla y sigue
        // con lo que falte al cerrarse.
        next();
        openInstaller();
        return;
      case 'camera': {
        setAsking(true);
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          // Aqui solo se pide el permiso: la camara se apaga en el acto.
          stream.getTracks().forEach((track) => track.stop());
        } catch {
          // Negada o sin camara: Configuracion lo dice y trae la guia.
        }
        setAsking(false);
        next();
        return;
      }
    }
  };

  return (
    <TourSheet
      step={step}
      index={index}
      total={total}
      asking={asking}
      onAllow={() => void allow()}
      onSkip={next}
      // Cerrar la hoja es "ahora no" a todo lo que faltaba. Mientras el cuadro
      // del sistema esta abierto no se cierra: la respuesta que llegue despues
      // la moveria a un paso que ya no existe.
      onClose={asking ? () => undefined : finish}
    />
  );
}

/**
 * La hoja de un paso, sin estado propio: lo que decide que paso toca vive arriba.
 * Va aparte para poder revisarla sola, en cada paso y a cada ancho.
 */
export function TourSheet({
  step,
  index,
  total,
  asking,
  onAllow,
  onSkip,
  onClose,
}: {
  step: TourStep;
  index: number;
  total: number;
  asking: boolean;
  onAllow: () => void;
  onSkip: () => void;
  onClose: () => void;
}): ReactNode {
  const copy = copyFor(step);
  return (
    <Modal
      open
      onClose={onClose}
      title="Configura este aparato"
      size="sm"
      footer={
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2">
          <Button size="lg" onClick={onSkip} disabled={asking}>
            Ahora no
          </Button>
          <Button variant="primary" size="lg" block loading={asking} onClick={onAllow}>
            {copy.action}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center px-2 py-1 text-center">
        {total > 1 ? (
          <p className="text-[12px] font-semibold uppercase tracking-wide text-muted" aria-live="polite">
            Paso {index + 1} de {total}
          </p>
        ) : null}
        <span className="mt-3 grid size-16 place-items-center rounded-2xl bg-primary-soft text-primary">
          {copy.icon}
        </span>
        <p className="mt-4 text-[18px] font-semibold text-balance">{copy.title}</p>
        <p className="mt-1.5 max-w-[22rem] text-[15px] text-pretty">{copy.body}</p>
        <p className="mt-3 max-w-[22rem] text-[13px] text-muted text-pretty">{copy.hint}</p>
      </div>
    </Modal>
  );
}
